/**
 * filter-tabs.js
 * -------------------------------------------------------------
 * Snitch style scrollable filter tabs for the collection page.
 *
 * It does NOT re-implement filtering. It rewrites the filter
 * query string and hands it to the theme's existing
 * FacetFiltersForm.renderPage(), so the product grid, product
 * count, sidebar facets and URL all update exactly the same way
 * they do when a sidebar checkbox is clicked.
 *
 * Single-select: one tab at a time. Clicking the active tab or
 * "ALL" clears every filter.
 *
 * No JS / facets.js missing -> the <a href> fallback just works.
 * -------------------------------------------------------------
 */
class FilterTabs extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector(".filter-tabs__viewport");
    this.tabs = Array.from(this.querySelectorAll(".filter-tabs__tab"));

    this.addEventListener("click", this.onClick);
    window.addEventListener("popstate", this.onPopState);

    this.scrollActiveIntoView(false);
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this.onPopState);
  }

  get canAjax() {
    return (
      typeof FacetFiltersForm !== "undefined" &&
      typeof FacetFiltersForm.renderPage === "function" &&
      document.getElementById("product-grid") !== null
    );
  }

  onClick(event) {
    const tab = event.target.closest(".filter-tabs__tab");
    if (!tab) return;

    // let the plain link handle it when the theme's facets JS is absent
    if (!this.canAjax) return;

    event.preventDefault();

    const params = new URLSearchParams(window.location.search);
    const clearAll =
      tab.hasAttribute("data-clear-all") || tab.classList.contains("is-active");

    // single-select: always start from a clean filter state
    Array.from(params.keys()).forEach((key) => {
      if (key.indexOf("filter.") === 0) params.delete(key);
    });
    params.delete("page");

    if (!clearAll) {
      params.append(tab.dataset.param, tab.dataset.value);
    }

    this.setActive(clearAll ? null : tab);
    this.setLoading(true);

    FacetFiltersForm.renderPage(params.toString());

    // facets.js has no "done" event, so release the lock once the
    // grid stops showing its loading overlay
    this.waitForGrid();
  }

  onPopState() {
    this.syncFromSearch(window.location.search);
  }

  /* ---------------- state helpers ---------------- */

  setActive(activeTab) {
    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-current", isActive ? "true" : "false");
    });

    // nothing selected -> highlight the ALL tab
    if (!activeTab) {
      const allTab = this.querySelector("[data-clear-all]");
      if (allTab) {
        allTab.classList.add("is-active");
        allTab.setAttribute("aria-current", "true");
      }
    }

    if (activeTab) this.scrollActiveIntoView(true);
  }

  syncFromSearch(search) {
    const params = new URLSearchParams(search);
    let matched = null;

    this.tabs.forEach((tab) => {
      if (tab.hasAttribute("data-clear-all")) return;
      const values = params.getAll(tab.dataset.param);
      if (values.indexOf(tab.dataset.value) !== -1) matched = tab;
    });

    this.setActive(matched);
  }

  setLoading(isLoading) {
    this.classList.toggle("filter-tabs--loading", isLoading);
  }

  waitForGrid() {
    const grid = document.getElementById("ProductGridContainer");
    if (!grid) return this.setLoading(false);

    let ticks = 0;
    const timer = setInterval(() => {
      const stillLoading = grid.querySelector(".collection.loading");
      if (!stillLoading || ++ticks > 40) {
        clearInterval(timer);
        this.setLoading(false);
      }
    }, 150);
  }

  scrollActiveIntoView(smooth) {
    const active = this.querySelector(".filter-tabs__tab.is-active");
    if (!active || !this.viewport) return;

    const target =
      active.offsetLeft - (this.viewport.clientWidth - active.offsetWidth) / 2;

    this.viewport.scrollTo({
      left: Math.max(target, 0),
      behavior: smooth ? "smooth" : "auto",
    });
  }
}

if (!customElements.get("filter-tabs")) {
  customElements.define("filter-tabs", FilterTabs);
}